import { MARKET_ROUND_TEAM_PAIRS, MARKET_ROUND_TEAM_PALETTE } from 'constants/marketRoundTeamColors'

export const TEAM_VISUAL_MAX_SLOTS = MARKET_ROUND_TEAM_PAIRS.length

/**
 * Normalize hex for comparison (e.g. #EC4D45 vs #ec4d45, #RGB).
 * @param {string} color
 * @returns {string|null}
 */
export function normalizeHexColor(color) {
  if (!color || typeof color !== 'string') return null
  let s = color.trim().toLowerCase()
  if (!s.startsWith('#')) s = `#${s}`
  if (s.length === 4 && /^#[0-9a-f]{3}$/i.test(s)) {
    s = `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`
  }
  return s.length === 7 && /^#[0-9a-f]{6}$/i.test(s) ? s : null
}

/**
 * Return 1-based slot if `color` matches a known company primary, else null.
 * @param {string} color
 * @returns {number|null}
 */
export function slotFromTeamColor(color) {
  const hex = normalizeHexColor(color)
  if (!hex) return null
  const idx = MARKET_ROUND_TEAM_PALETTE.findIndex((p) => normalizeHexColor(p) === hex)
  if (idx < 0) return null
  return idx + 1
}

/**
 * Assign each team a stable lobby slot (1..N) so colors stay consistent
 * across Lobby, Market Round, and Graphics.
 *
 * Priority: backend `position` → `color` matching palette → `Team N` / `COM N` name
 * → optional `prevSlotMap` for renamed teams (Lobby ref) → first free slot.
 *
 * @param {Array<object>} teams
 * @param {Map<string, number>|null} prevSlotMap previous id → slot (Lobby only)
 * @returns {Map<string, number>}
 */
export function buildTeamSlotMap(teams, prevSlotMap = null) {
  const map = new Map()
  const usedSlots = new Set()
  const maxSlot = TEAM_VISUAL_MAX_SLOTS

  if (!teams?.length) return map

  teams.forEach((team) => {
    if (team?.position != null) {
      const slot = Number(team.position)
      if (slot >= 1 && slot <= maxSlot && !usedSlots.has(slot)) {
        map.set(String(team.id), slot)
        usedSlots.add(slot)
      }
    }
  })

  teams.forEach((team) => {
    const id = String(team.id)
    if (map.has(id)) return
    const fromColor = slotFromTeamColor(team?.color)
    if (fromColor != null && fromColor >= 1 && fromColor <= maxSlot && !usedSlots.has(fromColor)) {
      map.set(id, fromColor)
      usedSlots.add(fromColor)
    }
  })

  teams.forEach((team) => {
    const id = String(team.id)
    if (map.has(id)) return
    const match = team?.name?.match(/^(?:Team|COM)\s+(\d+)$/i)
    if (match) {
      const slot = parseInt(match[1], 10)
      if (slot >= 1 && slot <= maxSlot && !usedSlots.has(slot)) {
        map.set(id, slot)
        usedSlots.add(slot)
      }
    }
  })

  if (prevSlotMap && typeof prevSlotMap.get === 'function') {
    teams.forEach((team) => {
      const id = String(team.id)
      if (map.has(id)) return
      const oldSlot = prevSlotMap.get(id)
      if (oldSlot != null && oldSlot >= 1 && oldSlot <= maxSlot && !usedSlots.has(oldSlot)) {
        map.set(id, oldSlot)
        usedSlots.add(oldSlot)
      }
    })
  }

  teams.forEach((team) => {
    const id = String(team.id)
    if (map.has(id)) return
    for (let s = 1; s <= maxSlot; s += 1) {
      if (!usedSlots.has(s)) {
        map.set(id, s)
        usedSlots.add(s)
        break
      }
    }
  })

  return map
}
